/**
 * Settings Panel Component
 *
 * Application preferences: language and notifications
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { accountService } from '../../services/accountService';

export function SettingsPanel() {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language || 'vi');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await accountService.getPreferences();
        setLanguage(prefs.language);
        if (prefs.language && prefs.language !== i18n.language) {
          i18n.changeLanguage(prefs.language);
        }
        setEmailNotifications(prefs.emailNotifications);
        setSmsNotifications(prefs.smsNotifications);
      } catch (error) {
        console.error('Failed to load preferences:', error);
      }
    };
    loadPreferences();
  }, [i18n]);

  const handleLanguageChange = async (value: string) => {
    try {
      setLanguage(value);
      i18n.changeLanguage(value);
      await accountService.updatePreferences({ language: value });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Failed to update language:', error);
    }
  };

  const handleEmailToggle = async (value: boolean) => {
    try {
      setEmailNotifications(value);
      await accountService.updatePreferences({ emailNotifications: value });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Failed to update email notifications:', error);
      setEmailNotifications(!value); // Revert on error
    }
  };

  const handleSmsToggle = async (value: boolean) => {
    try {
      setSmsNotifications(value);
      await accountService.updatePreferences({ smsNotifications: value });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Failed to update SMS notifications:', error);
      setSmsNotifications(!value); // Revert on error
    }
  };

  return (
    <section className='bg-surface-light dark:bg-surface-dark rounded-xl border border-border-light dark:border-border-dark p-6 shadow-sm flex flex-col h-full'>
      <h3 className='text-lg font-bold text-text-main dark:text-text-main-dark flex items-center gap-2 mb-6'>
        <span className='material-symbols-outlined text-primary'>tune</span>
        {t('account.settings.title')}
      </h3>

      {saveSuccess && (
        <div className='mb-4 rounded-lg bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 p-3'>
          <p className='text-xs text-green-700 dark:text-green-400'>
            {t('account.settings.success')}
          </p>
        </div>
      )}

      <div className='space-y-6 flex-1'>
        {/* Language */}
        <div className='flex items-center justify-between'>
          <div className='flex flex-col'>
            <span className='text-base font-medium text-text-main dark:text-text-main-dark'>
              {t('account.settings.language.label')}
            </span>
            <span className='text-sm text-text-secondary dark:text-text-secondary-dark'>
              {t('account.settings.language.description')}
            </span>
          </div>
          <select
            value={language}
            onChange={e => handleLanguageChange(e.target.value)}
            className='rounded-lg border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-main dark:text-text-main-dark text-sm py-2 pl-3 pr-8 focus:border-primary focus:ring-primary focus:outline-none'
          >
            <option value='vi'>Tiếng Việt</option>
            <option value='en'>English</option>
          </select>
        </div>

        <div className='h-px bg-border-light dark:bg-border-dark w-full'></div>

        {/* Email Notifications */}
        <div className='flex items-center justify-between'>
          <div className='flex flex-col'>
            <span className='text-base font-medium text-text-main dark:text-text-main-dark'>
              {t('account.settings.emailNotif.label')}
            </span>
            <span className='text-sm text-text-secondary dark:text-text-secondary-dark'>
              {t('account.settings.emailNotif.description')}
            </span>
          </div>
          <label className='relative inline-flex items-center cursor-pointer'>
            <input
              type='checkbox'
              checked={emailNotifications}
              onChange={e => handleEmailToggle(e.target.checked)}
              className='sr-only peer'
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
          </label>
        </div>

        {/* SMS Notifications */}
        <div className='flex items-center justify-between'>
          <div className='flex flex-col'>
            <span className='text-base font-medium text-text-main dark:text-text-main-dark'>
              {t('account.settings.smsNotif.label')}
            </span>
            <span className='text-sm text-text-secondary dark:text-text-secondary-dark'>
              {t('account.settings.smsNotif.description')}
            </span>
          </div>
          <label className='relative inline-flex items-center cursor-pointer'>
            <input
              type='checkbox'
              checked={smsNotifications}
              onChange={e => handleSmsToggle(e.target.checked)}
              className='sr-only peer'
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
          </label>
        </div>
      </div>
    </section>
  );
}
